import { useState } from 'react';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const b2cSolutions = [
  { name: 'EV Bike Rentals', path: 'ev-bike-rentals' },
  { name: 'Cab, Bike & Auto Booking', path: 'cab-bike-auto-booking' },
  { name: 'Bus Booking', path: 'bus-train-booking' },
  { name: 'Packers & Movers', path: 'packers-movers' },
  { name: 'Package Delivery', path: 'package-delivery' },
  { name: 'Railway Solutions', path: 'railway-solutions' },
  { name: 'EV Charger Installation', path: 'ev-charger-installation' }
];

const b2bSolutions = [
  { name: 'Fleet Management Platform', path: 'fleet-management' },
  { name: 'Contract Fleet & Rider Supply', path: 'contract-fleet' },
  { name: 'ONDC Delivery Solutions', path: 'ondc-delivery' },
  { name: 'Railway Logistics Integration', path: 'railway-logistics' },
  { name: 'Intercity Delivery Network', path: 'intercity-delivery' },
  { name: 'Industries Served', path: '../industries-served' }
];

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  
  const toggleMobileDropdown = (dropdown) => {
    if (activeMobileDropdown === dropdown) {
      setActiveMobileDropdown(null);
    } else {
      setActiveMobileDropdown(dropdown);
    }
  };

  // Premium brand colors with line animation and subtle upward shift
  const navTextClass = "relative text-[#3869B3] font-['TT_Firs_Neue_Trl',_'Plus_Jakarta_Sans',_sans-serif] hover:text-[#6FBF4C] font-medium text-[15px] xl:text-[16px] 2xl:text-[18.5px] whitespace-nowrap capitalize transition-colors duration-200 flex items-center after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-full after:h-[2px] after:bg-[#6FBF4C] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left";

  return (
    <header className="bg-white sticky top-0 z-50 h-[90px] xl:h-[110px] 2xl:h-[120px] flex items-center border-b border-gray-100 max-w-full">
      <div className="w-full px-4 xl:px-8 2xl:px-12">
        <div className="w-full max-w-[1800px] mx-auto flex justify-between items-center">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center mr-3 xl:mr-6 2xl:mr-10">
            <Link to="/">
              <img src="/logo.png" alt="Green Drive Mobility Logo" className="h-12 xl:h-16 2xl:h-20 w-auto object-contain" />
            </Link>
          </div>

          {/* Desktop Menu */}
          <nav className="hidden xl:flex gap-3 xl:gap-4.5 2xl:gap-7 items-center justify-center flex-1 min-w-0">
              <Link to="/about" className={navTextClass}>About Us</Link>
              <Link to="/services" className={navTextClass}>Services</Link>
              <Link to="/technologies" className={navTextClass}>Technologies</Link>
              <Link to="/green-drive-delivery" className={navTextClass}>Green Drive Delivery</Link>

              {/* Solutions Mega Menu */}
              <div className="group relative flex items-center h-full">
                <button className={`${navTextClass} gap-1 focus:outline-none`}>
                  Solutions <ChevronDown size={17} className="mt-[1px]" />
                </button>
                
                {/* Mega Menu Dropdown */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-[700px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="absolute -top-6 left-0 w-full h-6 bg-transparent"></div>
                  
                  <div className="bg-white rounded-lg shadow-xl border border-gray-100 p-8 grid grid-cols-2 gap-12 relative">
                    {/* B2C */}
                    <div>
                      <h3 className="text-[#3869B3] font-bold mb-4 uppercase tracking-wider text-sm border-b border-gray-100 pb-2">Consumer Solutions (B2C)</h3>
                      <ul className="space-y-3">
                        {b2cSolutions.map((item, idx) => (
                          <li key={idx}>
                            <Link to={`/solutions/b2c/${item.path}`} className="group/link flex items-center text-[15px] text-gray-700 hover:text-[#6FBF4C] transition-colors">
                              <ArrowRight size={14} className="opacity-0 -ml-4 group-hover/link:opacity-100 group-hover/link:ml-0 transition-all duration-200 mr-2 text-[#3869B3]" />
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* B2B */}
                    <div>
                      <h3 className="text-[#3869B3] font-bold mb-4 uppercase tracking-wider text-sm border-b border-gray-100 pb-2">Business Solutions (B2B)</h3>
                      <ul className="space-y-3">
                        {b2bSolutions.map((item, idx) => (
                          <li key={idx}>
                            <Link to={`/solutions/b2b/${item.path}`} className="group/link flex items-center text-[15px] text-gray-700 hover:text-[#6FBF4C] transition-colors">
                              <ArrowRight size={14} className="opacity-0 -ml-4 group-hover/link:opacity-100 group-hover/link:ml-0 transition-all duration-200 mr-2 text-[#3869B3]" />
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resources Dropdown */}
              <div className="group relative flex items-center h-full">
                <button className={`${navTextClass} gap-1 focus:outline-none`}>
                  Resources <ChevronDown size={17} className="mt-[1px]" />
                </button>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="absolute -top-6 left-0 w-full h-6 bg-transparent"></div>
                  <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                    {['News & Media', 'Articles & Insights', 'Case Study', 'Awards & Recognition'].map((item, idx) => (
                      <Link key={idx} to={item === 'News & Media' ? '/media' : `/resources/${item.toLowerCase().replace(/[ &]+/g, '-')}`} className="block px-5 py-2.5 text-[15px] text-gray-700 hover:bg-blue-50 hover:text-[#6FBF4C] transition-colors">
                        {item}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Partner Dropdown */}
              <div className="group relative flex items-center h-full">
                <button className={`${navTextClass} gap-1 focus:outline-none`}>
                  Partner With Us <ChevronDown size={17} className="mt-[1px]" />
                </button>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="absolute -top-6 left-0 w-full h-6 bg-transparent"></div>
                  <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                    {['Business Partnership', 'Careers', 'Become a Delivery Partner'].map((item, idx) => (
                      <Link key={idx} to={item === 'Careers' ? '/careers' : `/partner/${item.toLowerCase().replace(/[ &]+/g, '-')}`} className="block px-5 py-2.5 text-[15px] text-gray-700 hover:bg-blue-50 hover:text-[#6FBF4C] transition-colors">
                        {item}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <Link to="/investors" className={navTextClass}>Investors</Link>
            </nav>

            {/* Action Button */}
            <div className="hidden xl:flex items-center flex-shrink-0 ml-3 xl:ml-6 2xl:ml-10">
              <Link to="/contact" className="group relative overflow-hidden bg-[#3869B3] font-['TT_Firs_Neue_Trl',_'Plus_Jakarta_Sans',_sans-serif] text-white w-[118px] xl:w-[128px] 2xl:w-[150px] h-[38px] xl:h-[40px] 2xl:h-[46px] flex items-center justify-center rounded-[5px] font-normal text-[13.5px] xl:text-[14.5px] 2xl:text-[16px] transition-all duration-300 hover:shadow-lg hover:shadow-[#6FBF4C]/30 hover:-translate-y-0.5">
                <span className="absolute inset-0 w-full h-full bg-[#6FBF4C] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-out"></span>
                <span className="relative z-10 whitespace-nowrap">Contact Us</span>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="xl:hidden flex items-center">
              <button 
                onClick={toggleMobileMenu} 
                className="text-[#3869B3] hover:text-blue-900 focus:outline-none p-2 transition-colors"
              >
                {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
              </button>
            </div>
          </div>
        </div>


      {/* Mobile Menu Panel */}
      {isMobileMenuOpen && (
        <div className="xl:hidden bg-white border-b border-gray-100 absolute top-[90px] xl:top-[110px] 2xl:top-[120px] w-full left-0 max-h-[calc(100vh-90px)] overflow-y-auto shadow-xl z-40">
          <div className="px-4 py-4 space-y-1">
            <Link onClick={() => setIsMobileMenuOpen(false)} to="/about" className="block px-4 py-3 text-[16px] font-medium text-[#3869B3] hover:bg-blue-50 rounded-lg">About Us</Link>
            <Link onClick={() => setIsMobileMenuOpen(false)} to="/services" className="block px-4 py-3 text-[16px] font-medium text-[#3869B3] hover:bg-blue-50 rounded-lg">Services</Link>
            <Link onClick={() => setIsMobileMenuOpen(false)} to="/technologies" className="block px-4 py-3 text-[16px] font-medium text-[#3869B3] hover:bg-blue-50 rounded-lg">Technologies</Link>
            <Link onClick={() => setIsMobileMenuOpen(false)} to="/green-drive-delivery" className="block px-4 py-3 text-[16px] font-medium text-[#3869B3] hover:bg-blue-50 rounded-lg">Green Drive Delivery</Link>
            
            {/* Mobile Solutions */}
            <div>
              <button 
                onClick={() => toggleMobileDropdown('solutions')}
                className="w-full flex justify-between items-center px-4 py-3 text-[16px] font-medium text-[#3869B3] hover:bg-blue-50 rounded-lg"
              >
                Solutions
                <ChevronDown size={18} className={`transform transition-transform ${activeMobileDropdown === 'solutions' ? 'rotate-180' : ''}`} />
              </button>
              {activeMobileDropdown === 'solutions' && (
                <div className="pl-8 pb-2 space-y-4 pt-1">
                  <div>
                    <p className="text-xs font-bold text-[#3869B3] mb-2 uppercase tracking-widest">Consumer (B2C)</p>
                    <div className="space-y-2">
                      {b2cSolutions.map((item, idx) => (
                        <Link onClick={() => setIsMobileMenuOpen(false)} key={idx} to={`/solutions/b2c/${item.path}`} className="block text-[15px] text-gray-600">{item.name}</Link>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#3869B3] mb-2 uppercase tracking-widest mt-4">Business (B2B)</p>
                    <div className="space-y-2">
                      {b2bSolutions.map((item, idx) => (
                        <Link onClick={() => setIsMobileMenuOpen(false)} key={idx} to={`/solutions/b2b/${item.path}`} className="block text-[15px] text-gray-600">{item.name.replace(' Platform', '').replace(' Integration', '').replace(' Network', '')}</Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Resources */}
            <div>
              <button 
                onClick={() => toggleMobileDropdown('resources')}
                className="w-full flex justify-between items-center px-4 py-3 text-[16px] font-medium text-[#3869B3] hover:bg-blue-50 rounded-lg"
              >
                Resources
                <ChevronDown size={18} className={`transform transition-transform ${activeMobileDropdown === 'resources' ? 'rotate-180' : ''}`} />
              </button>
              {activeMobileDropdown === 'resources' && (
                <div className="pl-8 space-y-2 pb-2 pt-1">
                  {['News & Media', 'Articles & Insights', 'Awards & Recognition'].map((item, idx) => (
                    <Link onClick={() => setIsMobileMenuOpen(false)} key={idx} to={item === 'News & Media' ? '/media' : `/resources/${item.toLowerCase().replace(/[ &]+/g, '-')}`} className="block text-[15px] text-gray-600">{item}</Link>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Partner */}
            <div>
              <button 
                onClick={() => toggleMobileDropdown('partner')}
                className="w-full flex justify-between items-center px-4 py-3 text-[16px] font-medium text-[#3869B3] hover:bg-blue-50 rounded-lg"
              >
                Partner With Us
                <ChevronDown size={18} className={`transform transition-transform ${activeMobileDropdown === 'partner' ? 'rotate-180' : ''}`} />
              </button>
              {activeMobileDropdown === 'partner' && (
                <div className="pl-8 space-y-2 pb-2 pt-1">
                  {['Business Partnership', 'Careers', 'Become a Delivery Partner'].map((item, idx) => (
                    <Link onClick={() => setIsMobileMenuOpen(false)} key={idx} to={item === 'Careers' ? '/careers' : `/partner/${item.toLowerCase().replace(/[ &]+/g, '-')}`} className="block text-[15px] text-gray-600">{item}</Link>
                  ))}
                </div>
              )}
            </div>

            <Link onClick={() => setIsMobileMenuOpen(false)} to="/investors" className="block px-4 py-3 text-[16px] font-medium text-[#3869B3] hover:bg-blue-50 rounded-lg">Investors</Link>
            
            <div className="pt-4 pb-4 px-4">
              <Link onClick={() => setIsMobileMenuOpen(false)} to="/contact" className="block text-center w-full bg-[#3869B3] text-white px-6 py-3 rounded-md font-semibold text-[16px]">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
